import { useState, useEffect, useRef } from "react";
import { X, Send } from "lucide-react";
import mjml2html from "mjml-browser";
import { colors, fonts } from "../../styles/theme";
import { useTenant } from "../../contexts/TenantContext";
import { Button } from "../../components/ui";
import EmailEditor from "../../components/EmailEditor";
import { getWebcastHighlightsTemplate } from "../../data/templates";
import { fetchEventContacts, sendZoomInvitations } from "../../services/api";
import { inputStyle, labelStyle } from "./constants";

export default function PostEventEmailModal({ event, recordings, onClose, onSent }) {
  const tenant = useTenant();
  const [segment, setSegment] = useState("all_invited");
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [assets, setAssets] = useState({ replayUrl: "", slidesUrl: "", transcriptUrl: "", youtubeUrl: "" });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [subject, setSubject] = useState("");
  const editorRef = useRef(null);

  // Auto-populate replay URL from recordings
  useEffect(() => {
    const replay = (recordings || []).find((r) => r.playUrl);
    if (replay) setAssets((a) => ({ ...a, replayUrl: a.replayUrl || replay.playUrl }));
  }, [recordings]);

  // Load contacts when segment changes
  useEffect(() => {
    setLoadingContacts(true);
    fetchEventContacts(event.id, segment)
      .then((data) => setContacts(data.contacts || []))
      .catch(() => setContacts([]))
      .finally(() => setLoadingContacts(false));
  }, [event.id, segment]);

  // Set default subject
  useEffect(() => {
    const tpl = getWebcastHighlightsTemplate(tenant, event, assets);
    setSubject(tpl.subject);
  }, []);

  const handleSend = async () => {
    if (contacts.length === 0) return;
    const editorHtml = editorRef.current?.getHTML?.() || "";
    if (!editorHtml.trim()) {
      alert("Email body is empty");
      return;
    }

    const tpl = getWebcastHighlightsTemplate(tenant, event, assets);
    const mjmlString = tpl.mjml.replace("{{BODY}}", editorHtml).replace("{{PREHEADER}}", subject);
    let compiledHtml;
    try {
      const compiled = mjml2html(mjmlString, { validationLevel: "soft" });
      compiledHtml = compiled.html;
    } catch {
      alert("Failed to compile email template");
      return;
    }

    if (!window.confirm(`Send highlights email to ${contacts.length} contact${contacts.length !== 1 ? "s" : ""} (${segment.replace(/_/g, " ")})?`)) return;

    setSending(true);
    try {
      const data = await sendZoomInvitations(event.id, {
        contacts,
        emailHtml: compiledHtml,
        emailSubject: subject,
        sendMethod: "sendgrid",
      });
      setResult(`Sent to ${data.sent} of ${data.total} contacts`);
      setTimeout(() => onSent(), 2000);
    } catch (err) {
      setResult(`Error: ${err.message}`);
    } finally {
      setSending(false);
    }
  };

  const tpl = getWebcastHighlightsTemplate(tenant, event, assets);

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200 }} />
      <div style={{ position: "fixed", top: "5%", left: "50%", transform: "translateX(-50%)", width: 680, maxHeight: "90vh", background: colors.bgCard, borderRadius: 12, border: `1px solid ${colors.border}`, zIndex: 201, display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        {/* Header */}
        <div style={{ padding: "20px 24px", borderBottom: `1px solid ${colors.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h3 style={{ margin: 0, fontFamily: fonts.heading, fontSize: 18, color: colors.text }}>Send Highlights Email</h3>
            <span style={{ fontSize: 12, color: colors.textSecondary }}>{event.title}</span>
          </div>
          <X size={20} color={colors.textSecondary} style={{ cursor: "pointer" }} onClick={onClose} />
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          {/* Segment picker */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Audience</label>
            <div style={{ display: "flex", gap: 6 }}>
              {[
                { id: "attended", label: "Attended" },
                { id: "not_attended", label: "Did Not Attend" },
                { id: "all_invited", label: "All Invited" },
              ].map((s) => (
                <button
                  key={s.id}
                  onClick={() => setSegment(s.id)}
                  style={{
                    padding: "6px 14px", borderRadius: 6, fontSize: 12, fontWeight: 600, fontFamily: fonts.body,
                    border: `1px solid ${segment === s.id ? colors.accent : colors.border}`,
                    background: segment === s.id ? colors.accentDim : "transparent",
                    color: segment === s.id ? colors.accent : colors.textSecondary,
                    cursor: "pointer",
                  }}
                >
                  {s.label}
                </button>
              ))}
              <span style={{ fontSize: 12, color: colors.textSecondary, alignSelf: "center", marginLeft: 8 }}>
                {loadingContacts ? "Loading..." : `${contacts.length} recipient${contacts.length !== 1 ? "s" : ""}`}
              </span>
            </div>
          </div>

          {/* Asset URLs */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
            {[
              { key: "replayUrl", label: "Replay URL" },
              { key: "slidesUrl", label: "Slides PDF URL" },
              { key: "transcriptUrl", label: "Transcript URL" },
              { key: "youtubeUrl", label: "YouTube Highlights URL" },
            ].map((f) => (
              <div key={f.key}>
                <label style={{ ...labelStyle, fontSize: 10 }}>{f.label}</label>
                <input
                  type="text"
                  value={assets[f.key]}
                  onChange={(e) => setAssets((a) => ({ ...a, [f.key]: e.target.value }))}
                  placeholder={`Paste ${f.label.toLowerCase()}...`}
                  style={{ ...inputStyle, fontSize: 11, padding: "6px 10px" }}
                />
              </div>
            ))}
          </div>

          {/* Subject */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Subject</label>
            <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} style={inputStyle} />
          </div>

          {/* Email body editor */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Email Body</label>
            <div style={{ border: `1px solid ${colors.border}`, borderRadius: 8, minHeight: 200 }}>
              <EmailEditor ref={editorRef} initialContent={tpl.defaultContent} />
            </div>
          </div>

          {result && (
            <div style={{ fontSize: 12, color: result.startsWith("Error") ? colors.danger : colors.success, marginBottom: 12, padding: "8px 12px", background: result.startsWith("Error") ? "rgba(239,68,68,0.08)" : "rgba(16,185,129,0.08)", borderRadius: 6 }}>
              {result}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "16px 24px", borderTop: `1px solid ${colors.border}`, display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button variant="default" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" icon={Send} onClick={handleSend} disabled={sending || contacts.length === 0}>
            {sending ? "Sending..." : `Send to ${contacts.length} Contact${contacts.length !== 1 ? "s" : ""}`}
          </Button>
        </div>
      </div>
    </>
  );
}
